import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useStore } from "../../../../helpers";
import { FACET_CREATE_DEFAULT_VERSION } from "../../tars-constants";
import { useCreateFacet, useFacetsAutogenerateEtl } from "../../tars-hooks";
import { buildDsForFacetAutogenerate } from "./build-facet-autogenerate-ds";
import { safe_get } from "../../../../report-visuals";

export function useAnalyticsFacetActions(
    notebookIdNum: number,
    sourceId: number,
    canvasId: number,
    selectedSourceRaw: string | undefined
) {
    const { tarsAnalyticsStore, tarsStore } = useStore();
    const queryClient = useQueryClient();
    const createFacet = useCreateFacet();
    const facetsAutogenerateEtl = useFacetsAutogenerateEtl();

    const handleAddFacetManually = () => {
        if (notebookIdNum <= 0 || canvasId <= 0 || sourceId <= 0) {
            toast.error("Select a notebook, canvas, and data source.");
            return;
        }
        const newFacetName = `Facet ${tarsAnalyticsStore.facetCount + 1}`;
        tarsAnalyticsStore.addFacet({ name: newFacetName });
        tarsAnalyticsStore.setAnalyticsToolbarMode("json");
        const selectedFacetId = tarsAnalyticsStore.selectedFacetId;

        createFacet.mutate(
            {
                notebook_id: notebookIdNum,
                source_id: sourceId,
                canvas_id: canvasId,
                etl_json: {},
                facet_result: {},
                version: FACET_CREATE_DEFAULT_VERSION,
                more: {},
            },
            {
                onSuccess: (resp) => {
                    console.log("useAnalyticsFacetActions", resp);
                    if (!selectedFacetId) return;
                    const facetId = safe_get(resp, "id", undefined);
                    if (!facetId) return;
                    tarsAnalyticsStore.updateFacet(selectedFacetId, { facet_id: facetId });
                },
            }
        );
    };

    const handleGenerateFacet = (matrixKeys: string[]) => {
        if (notebookIdNum <= 0 || canvasId <= 0 || sourceId <= 0) {
            toast.error("Select a notebook, canvas, and data source.");
            return;
        }
        if (matrixKeys.length === 0) {
            toast.error("Select at least one source field key.");
            return;
        }
        const dsResult = buildDsForFacetAutogenerate(
            (id) => tarsStore.getNode(id),
            selectedSourceRaw,
            matrixKeys
        );
        if ("errorMessage" in dsResult) {
            toast.error(dsResult.errorMessage);
            return;
        }
        facetsAutogenerateEtl.mutate(
            {
                ds: dsResult.ds,
                notebook_id: notebookIdNum,
                source_id: sourceId,
                canvas_id: canvasId,
                persist_facets: true,
                decimal_places: 1,
            },
            {
                onSuccess: () => {
                    void queryClient.invalidateQueries({ queryKey: ["tars", "facets", "list"] });
                },
            }
        );
    };

    return {
        handleAddFacetManually,
        handleGenerateFacet,
        generateFacetLoading: facetsAutogenerateEtl.isLoading,
    };
}
